import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ToolsMissingProceduresPageStore } from './tools-missing-procedures-page.store';
import { FormBuilder } from '@angular/forms';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import {
    AddressDetailObject,
    PersonAddressObject,
    PersonGridItemObject,
    ToolsMissingProcedureGridItemObject,
    WorkGridItemObject,
} from '@it2go/types';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { doctorChipField, userClientChipColumn } from '../../../../shared/components/kit/table/table.fields';
import { Router } from '@angular/router';
import { FilterInput } from '@it2go/data-grid';
import {
    ProcedureValidatorErrorEnum,
} from '../../../../../../../../services/src/domain/service/procedure-validation/types/procedure-validator.error-enum';
import { folderUri, workUri } from '../../../../../utils/router.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { toolMissingProceduresFrag } from './tools-missing-procedures-page.query';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { PersonInsuranceObject } from '@libs/types/src/services/object-type/person/person-insurance.object';
import { getValid } from '@libs/shared/utils/get-valid';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import {
    DateScrollerInputComponent,
} from '../../../../../components/kit/input/date-scroller-input/date-scroller-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-tools-missing-procedures-page',
    templateUrl: './tools-missing-procedures-page.component.html',
    styleUrls: ['./tools-missing-procedures-page.component.scss'],
    providers: [ToolsMissingProceduresPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        DatePipe,
        DateScrollerInputComponent,
        FormatPersonPipe,
        PrimeTemplate,
        SelectInputComponent,
        TableComponent,
        TranslatePipe,
    ],
})
export class ToolsMissingProceduresPageComponent extends SubscriptionComponent {

    protected readonly store = inject(ToolsMissingProceduresPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);

    form = this.fb.group({
        serviceId: this.fb.control<number | null>(null),
        workplaceId: this.fb.control<number | null>(null),
        dateFrom: this.fb.control<Date | null>(new Date()),
        dateTo: this.fb.control<Date | null>(new Date()),
    });

    filters: FilterInput[][] = [];
    columns: TableColumn[] = [
        { name: 'createVisit80090', exportAs: null },
        { name: 'createVisit80091', exportAs: null },
        {
            ...userClientChipColumn,
            name: 'personName',
            dataKey: 'folder.person',
            data: (person: PersonGridItemObject) => {
                return {
                    ...person,
                    address: this.getCurrentAddress(person.addresses),
                    birthDate: person.birthdate,
                };
            },
            commandCallback: (item: ToolsMissingProcedureGridItemObject) => this.goToFolderDetail(item.folder?.id || 0, item.folder?.serviceId),
            filterable: { type: 'text' },
        },
        {
            name: 'date',
            type: TableColumnType.Date,
        },
        {
            name: 'day',
            dataKey: 'date',
            type: TableColumnType.Day,
        },
        {
            name: 'workDay',
            dataKey: 'date',
            type: TableColumnType.Bool,
            data: (date: string) => new Date(date).getDay() % 6 !== 0,
        },
        {
            name: 'weekend',
            dataKey: 'date',
            type: TableColumnType.Bool,
            data: (date: string) => new Date(date).getDay() % 6 === 0,
        },
        {
            name: 'insuranceNumber',
            dataKey: 'folder.person.insuranceNumber',
            filterable: { type: 'text' },
        },
        {
            name: 'insuranceAbbr',
            dataKey: null,
            data: (item: ToolsMissingProcedureGridItemObject) => {
                return getValid<PersonInsuranceObject>(item.folder?.person?.insurances, new Date(item.date), 'insured')?.insuranceAbbr;
            },
            filterable: { type: 'text' },
        },
        {
            name: 'city',
            dataKey: 'folder.person.addresses',
            data: (addresses: PersonAddressObject[]) => this.getCurrentCity(addresses),
        },
        {
            name: 'address',
            dataKey: 'folder.person.addresses',
            type: TableColumnType.MapLink,
            data: (addresses: PersonAddressObject[]) => this.getCurrentAddress(addresses),
        },
        {
            name: 'generalGroups',
        },
        {
            name: 'anamnesisFrom',
            dataKey: 'folder.anamnesis.dateFrom',
            type: TableColumnType.Date,
        },
        {
            name: 'anamnesisTo',
            dataKey: 'folder.anamnesis.dateTo',
            type: TableColumnType.Date,
        },
        {
            name: 'visitsThisDay',
            dataKey: null,
            filterable: { type: 'text' },
            exportAs: (item: any) => (item.works || []).length,
        },
        doctorChipField('doctor', {
            dataKey: 'folder.anamnesis.doctor',
        }),
    ];

    protected serviceIdChange = toSignal(this.form.controls.serviceId.valueChanges);

    constructor() {
        super();
        effect(() => {
            const services = this.store.services();
            if (!services.length) return;

            const selectedServiceId = this.store.globalGlobalStore.loggedUserStore.selectedServiceId();
            const serviceId = services.some((service) => service.id === selectedServiceId)
                ? selectedServiceId
                : services[0].id;

            if (this.form.controls.serviceId.value !== serviceId) {
                this.form.controls.serviceId.setValue(serviceId);
            }
        });
        effect(() => {
            this.store.globalGlobalStore.loggedUserStore.setSelectedServiceId(this.serviceIdChange()).then();
        });
        this.subs.push(
            this.form.valueChanges.subscribe((newValues) => {
                const dateFrom = newValues.dateFrom;
                const dateTo = newValues.dateTo;
                const serviceId = newValues.serviceId;
                const workplaceId = newValues.workplaceId;
                const filters = [
                    [{
                        column: 'error',
                        operator: 'EQ',
                        value: [ProcedureValidatorErrorEnum.MISSING_PROCEDURE_80090_80091],
                    }],
                    serviceId ? [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }] : null,
                    (dateFrom && dateTo) ? [{
                        column: 'dateRange',
                        operator: 'BETWEEN',
                        value: [dateFrom.toISOString(), dateTo.toISOString()],
                    }] : null,
                ].filter((it) => !!it);
                if (workplaceId) {
                    filters.push([{ column: 'workplaceId', operator: 'EQ', value: [workplaceId.toString()] }]);
                }
                this.filters = filters as any;
            }),
            this.form.controls.serviceId.valueChanges.subscribe((serviceId) => {
                if (serviceId) {
                    this.loadWorkplaces(serviceId);
                }
            }),
        );
    }

    openWorkDetail(work: WorkGridItemObject) {
        this.router.navigateByUrl(workUri(work, work.serviceId));
    }

    openCreateVisit(item: ToolsMissingProcedureGridItemObject, procedure: string) {
        this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.VISIT }, item.folder!.serviceId),
            {
                state: {
                    serviceId: this.form.value.serviceId,
                    personId: item.folder!.personId,
                    dateFrom: new Date(item.date),
                    medicalProcedures: [{
                        prescribedId: null,
                        serviceMedicalProcedureId: null,
                        code: procedure,
                        name: '',
                        description: '',
                        applications: 1,
                    }],
                },
            },
        );
    }

    private goToFolderDetail(folderId: number, serviceId: number | null | undefined) {
        this.router.navigateByUrl(folderUri(folderId, serviceId));
    }

    private getCurrentCity(addresses: PersonAddressObject[]) {
        const currentAddress = this.getCurrentAddress(addresses);
        return currentAddress?.regionalStructure?.find((it) => {
            return it.type === 'regional.municipality';
        })?.name ?? '';
    }

    private getCurrentAddress(addresses: PersonAddressObject[]): AddressDetailObject | null {
        const currentDate = new Date();
        return addresses.find((address) => {
            if (!address.validFrom) return false;
            const validFrom = new Date(address.validFrom);
            const validTo = address.validTo ? new Date(address.validTo) : null;
            return validFrom <= currentDate && (!validTo || validTo >= currentDate);
        })?.address ?? null;
    }

    private loadWorkplaces(serviceId: number) {
        this.store.api.getWorkplaces({
            filter: [
                [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }],
                [{ column: 'dateFrom', operator: 'LTE', value: [new Date().toISOString()] }],
                [
                    { column: 'dateTo', operator: 'GTE', value: [new Date().toISOString()] },
                    { column: 'dateTo', operator: 'EMPTY', value: ['1'] },
                ],
            ],
            sorter: [{ column: 'name', direction: 'ASC' }],
            paging: { page: 1, itemsPerPage: 999 },
        });
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
    protected readonly toolMissingProceduresFrag = toolMissingProceduresFrag;
}
